import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight } from '@phosphor-icons/react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CaseStudy() {
  const { t } = useLanguage();
  const [caseStudies, setCaseStudies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedIndustry, setSelectedIndustry] = useState('all');

  useEffect(() => {
    const fetchCaseStudies = async () => {
      try {
        const response = await axios.get(`${API}/case-studies?active_only=true`);
        setCaseStudies(response.data);
      } catch (error) {
        console.error('Error fetching case studies:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchCaseStudies();
  }, []);

  const industries = ['all', ...new Set(caseStudies.map(s => s.industry))];
  
  const filteredStudies = selectedIndustry === 'all' 
    ? caseStudies 
    : caseStudies.filter(s => s.industry === selectedIndustry);

  return (
    <div className="min-h-screen pt-20" data-testid="case-study-page">
      {/* Header */}
      <section className="py-20 lg:py-28 bg-xgrids-bg-secondary">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <p className="font-mono text-xgrids-brand-primary text-sm tracking-widest uppercase mb-4">Case Study</p>
            <h1 className="font-heading text-4xl lg:text-5xl font-bold text-white mb-6" data-testid="case-study-title">
              {t('caseStudy.title')}
            </h1>
            <p className="text-xgrids-text-secondary text-lg max-w-2xl">
              {t('caseStudy.desc')}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Filter */}
      <section className="py-8 border-b border-white/10 sticky top-16 lg:top-20 bg-xgrids-bg-primary z-30">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex gap-4 overflow-x-auto pb-2">
            {industries.map(industry => (
              <button
                key={industry}
                onClick={() => setSelectedIndustry(industry)}
                className={`px-4 py-2 text-sm font-medium whitespace-nowrap transition-colors ${
                  selectedIndustry === industry
                    ? 'bg-xgrids-brand-primary text-xgrids-bg-primary'
                    : 'bg-xgrids-bg-surface text-xgrids-text-secondary hover:text-white border border-white/10'
                }`}
                data-testid={`filter-${industry}`}
              >
                {industry === 'all' ? t('caseStudy.allIndustries') : industry}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Case Studies Grid */}
      <section className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          {loading ? (
            <div className="text-center py-20">
              <div className="animate-pulse text-xgrids-text-secondary">{t('common.loading')}</div>
            </div>
          ) : filteredStudies.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-xgrids-text-secondary">{t('caseStudy.noData')}</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {filteredStudies.map((study, index) => (
                <motion.article
                  key={study.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="card-hover bg-xgrids-bg-surface rounded-sm overflow-hidden group"
                  data-testid={`case-study-item-${index}`}
                >
                  <div className="aspect-video overflow-hidden">
                    <img
                      src={study.image_url}
                      alt={study.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-8">
                    <span className="font-mono text-xgrids-brand-blue text-xs tracking-widest uppercase">
                      {study.industry}
                    </span>
                    <h2 className="font-heading text-xl lg:text-2xl font-bold text-white mt-3 mb-4">
                      {study.title}
                    </h2>
                    <p className="text-xgrids-text-secondary mb-6">
                      {study.description}
                    </p>
                    <div className="prose prose-invert prose-sm max-w-none text-xgrids-text-secondary leading-relaxed">
                      <p>{study.content}</p>
                    </div>
                  </div>
                </motion.article>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 lg:py-28 bg-xgrids-bg-secondary">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-2xl lg:text-3xl font-bold text-white mb-6">
              {t('caseStudy.similarProject')}
            </h2>
            <p className="text-xgrids-text-secondary mb-8">
              {t('caseStudy.similarProjectDesc')}
            </p>
            <Link
              to="/tentang#contact"
              className="btn-primary rounded-none inline-flex items-center gap-2"
              data-testid="case-study-cta-btn"
            >
              {t('caseStudy.consultProject')}
              <ArrowRight size={18} />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
